/* eslint-disable new-cap */

/**
 * renderTemplateToPDF.ts
 *
 * Renders a ReportTemplate + values into a text-selectable jsPDF document.
 * The layout mirrors the HTML preview exactly: panels as columns, sections
 * stacked vertically within each panel, all styled via the Theme.
 *
 * UNIT CONVENTION
 * ----------------
 * Every number that flows through the render* functions below — rect
 * coordinates, theme font sizes, theme.spacing values, padding, gaps,
 * image heights, line widths, everything — is in the SAME template px
 * units used by the HTML preview (A4_PX). There is exactly ONE place in
 * this file that knows about jsPDF's pt coordinate space: the `PdfPx`
 * class. It wraps the handful of jsPDF drawing primitives we use and
 * multiplies by PX_TO_PT right before the value reaches jsPDF.
 *
 * Do not call `px()` anywhere outside `PdfPx`. If you find yourself
 * tempted to, it almost always means a new primitive needs a method on
 * `PdfPx` instead.
 *
 * Usage:I want to
 *   import { renderTemplateToPDF } from './renderTemplateToPDF';
 *   const doc = renderTemplateToPDF(template, valuesMap);
 *   doc.save('report.pdf');
 */

import type {
  Field,
  FieldGroupSection,
  FieldState,
  FreeTextSection,
  HeaderSection,
  ImageGridSection,
  Panel,
  Rect,
  ReportTemplate,
  RichTextSection,
  Section,
  StatRowSection,
  TableSection,
  Theme,
} from './report-typing';

import { jsPDF } from 'jspdf';

// ─── Constants ───────────────────────────────────────────────────────────────

/** A4 in pt (jsPDF default unit) */
const A4_PT = { width: 595.28, height: 841.89 };

/** Px defined in SizeToDims for a4 — this is the template/HTML coordinate system */
const A4_PX = { width: 794, height: 1123 };

/** Global scale factor: px (template coords) → pt (PDF coords) */
const PX_TO_PT = A4_PT.width / A4_PX.width; // ≈ 0.7496

/** Convert a px value from the template coordinate system to pt for jsPDF. */
function px(value: number): number {
  return value * PX_TO_PT;
}

/** Parse a hex color string into [r, g, b] (0–255) */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const bigint = Number.parseInt(h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

/** Guess the image format from a data URI or base64 string */
function guessImageFormat(data: string): string {
  if (data.startsWith('data:image/jpeg') || data.startsWith('data:image/jpg'))
    return 'JPEG';
  if (data.startsWith('data:image/png')) return 'PNG';
  if (data.startsWith('data:image/webp')) return 'WEBP';
  if (
    data.toLowerCase().endsWith('.jpg')
    || data.toLowerCase().endsWith('.jpeg')
  ) {
    return 'JPEG';
  }
  if (data.toLowerCase().endsWith('.png')) return 'PNG';
  if (data.toLowerCase().endsWith('.webp')) return 'WEBP';
  // If raw base64, default to PNG
  return 'PNG';
}

// ─── PdfPx: the one and only px→pt conversion boundary ──────────────────────

interface TextOptions { charSpace?: number }

/**
 * Thin wrapper around a jsPDF instance. Every method takes arguments in
 * template px units and converts to pt internally, immediately before
 * calling the real jsPDF API. Render functions should never see a `jsPDF`
 * instance directly — only this wrapper.
 */
class PdfPx {
  constructor(private readonly doc: jsPDF) {}

  /** Escape hatch for the handful of callers that need the raw jsPDF doc (e.g. .save()). */
  get raw(): jsPDF {
    return this.doc;
  }

  setFont(family: string, style: string) {
    this.doc.setFont(family, style);
  }

  setFontSize(sizePx: number) {
    this.doc.setFontSize(px(sizePx));
  }

  setTextColor(hex: string) {
    const [r, g, b] = hexToRgb(hex);
    this.doc.setTextColor(r, g, b);
  }

  setFillColor(hex: string) {
    const [r, g, b] = hexToRgb(hex);
    this.doc.setFillColor(r, g, b);
  }

  setDrawColor(hex: string) {
    const [r, g, b] = hexToRgb(hex);
    this.doc.setDrawColor(r, g, b);
  }

  setLineWidth(widthPx: number) {
    this.doc.setLineWidth(px(widthPx));
  }

  setLineDashPattern(patternPx: number[], phasePx: number) {
    this.doc.setLineDashPattern(patternPx.map(px), px(phasePx));
  }

  text(
    text: string | string[],
    xPx: number,
    yPx: number,
    options?: TextOptions,
  ) {
    const resolvedOptions
      = options?.charSpace !== undefined
        ? { ...options, charSpace: px(options.charSpace) }
        : options;
    this.doc.text(text, px(xPx), px(yPx), resolvedOptions);
  }

  line(x1Px: number, y1Px: number, x2Px: number, y2Px: number) {
    this.doc.line(px(x1Px), px(y1Px), px(x2Px), px(y2Px));
  }

  rect(xPx: number, yPx: number, wPx: number, hPx: number, style?: string) {
    this.doc.rect(px(xPx), px(yPx), px(wPx), px(hPx), style);
  }

  addImage(
    data: string,
    format: string,
    xPx: number,
    yPx: number,
    wPx: number,
    hPx: number,
  ) {
    this.doc.addImage(
      data,
      format,
      px(xPx),
      px(yPx),
      px(wPx),
      px(hPx),
      undefined,
      'FAST',
    );
  }

  /** maxWidthPx is converted; the returned lines are plain strings (no units). */
  splitTextToSize(text: string, maxWidthPx: number): string[] {
    return this.doc.splitTextToSize(text, px(maxWidthPx)) as string[];
  }

  /** jsPDF measures in pt — convert back to px so callers can keep doing px math. */
  getTextWidth(text: string): number {
    return this.doc.getTextWidth(text) / PX_TO_PT;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Resolve a field value from the store values map */
function resolveValue(
  values: Record<string, FieldState>,
  field: Field,
): string {
  const state = values[field.key];
  if (
    state?.value !== null
    && state?.value !== undefined
    && state?.value !== ''
  ) {
    return String(state.value);
  }
  if (
    field.defaultValue !== null
    && field.defaultValue !== undefined
    && field.defaultValue !== ''
  ) {
    return String(field.defaultValue);
  }
  return '';
}

/** Resolve a dropdown value to its human-readable label */
function resolveDropdownLabel(
  values: Record<string, FieldState>,
  field: Field,
): string {
  const raw = resolveValue(values, field);
  if (field.options && field.options.length > 0) {
    const match = field.options.find(o => o.id === raw);
    if (match) return match.label;
  }
  return raw || '—';
}

/**
 * Map theme font weight to a jsPDF font style string.
 * jsPDF built-in Helvetica supports: 'normal', 'bold', 'italic', 'bolditalic'
 */
function fontStyle(weight: number): string {
  return weight >= 600 ? 'bold' : 'normal';
}

/** Apply a theme font spec to the doc (size is in px, like everything else here) */
function applyFont(
  pdf: PdfPx,
  font: { family: string; size: number; weight: number },
  color: string,
) {
  pdf.setFont(font.family, fontStyle(font.weight));
  pdf.setFontSize(font.size);
  pdf.setTextColor(color);
}

/** Calculate the block height of a text block containing `lineCount` lines. */
function getTextBlockHeight(
  lineCount: number,
  fontSize: number,
  lineSpacingFactor = 1.25,
): number {
  if (lineCount <= 0) return 0;
  return (lineCount - 1) * fontSize * lineSpacingFactor + fontSize;
}

// ─── Section Title Helper ────────────────────────────────────────────────────

/**
 * Renders a section title with underline, returns the Y position after it.
 * All inputs/outputs are in px.
 */
function renderSectionTitle(
  pdf: PdfPx,
  title: string,
  theme: Theme,
  x: number,
  y: number,
  width: number,
): number {
  const sl = theme.fonts.sectionLabel;
  const displayTitle = sl.uppercase ? title.toUpperCase() : title;

  pdf.setFont(sl.family, fontStyle(sl.weight));
  pdf.setFontSize(sl.size);
  pdf.setTextColor(theme.colors.muted);

  if (sl.letterSpacing && sl.letterSpacing > 0) {
    pdf.text(displayTitle, x, y, { charSpace: sl.letterSpacing });
  }
  else {
    pdf.text(displayTitle, x, y);
  }

  // Underline
  const lineY = y + 4;
  pdf.setDrawColor(theme.colors.border);
  pdf.setLineWidth(1);
  pdf.line(x, lineY, x + width, lineY);

  return lineY + sl.size + 4;
}

// ─── Section Renderers ───────────────────────────────────────────────────────
// Each returns the new Y position after rendering its content. All inputs
// and outputs are in px — see the PdfPx note at the top of the file.

function getLogoDims(
  logo: { width?: number; height?: number } | null | undefined,
  targetHeight: number,
): { width: number; height: number } {
  if (!logo) {
    return { width: 0, height: 0 };
  }
  const h = targetHeight;
  if (logo.width && logo.height) {
    const ratio = logo.width / logo.height;
    return { width: h * ratio, height: h };
  }
  // Default fallback ratio of 2.5 if dimensions are unknown
  return { width: h * 2.5, height: h };
}

function getResolvedUrl(url: string): string {
  if (!url) return '';
  if (
    url.startsWith('data:')
    || url.startsWith('http:')
    || url.startsWith('https:')
  ) {
    return url;
  }
  const baseUrl = (import.meta.env && import.meta.env.BASE_URL) || '/';
  if (baseUrl !== '/' && url.startsWith(baseUrl)) {
    return url;
  }
  return (baseUrl + url).replace(/\/+/g, '/');
}

function renderHeaderSection(
  pdf: PdfPx,
  section: HeaderSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Heading text
  applyFont(pdf, theme.fonts.heading, theme.colors.text);
  pdf.text(section.heading, rect.x, y + theme.fonts.heading.size);
  y += theme.fonts.heading.size;

  const targetHeight = section.logoHeight || 50;
  const gap = 16;

  // Resolve logos
  const defaultLogo = section.logo;
  let { addedLogo } = section;
  const dynamicAddedLogo = values['header.addedLogo']?.value as { url: string; width?: number; height?: number } | null;
  if (
    dynamicAddedLogo
    && typeof dynamicAddedLogo === 'object'
    && dynamicAddedLogo.url
  ) {
    addedLogo = dynamicAddedLogo;
  }

  // Calculate dims
  const defaultDims = defaultLogo
    ? getLogoDims(defaultLogo, targetHeight)
    : { width: 0, height: 0 };
  const addedDims = addedLogo
    ? getLogoDims(addedLogo, targetHeight)
    : { width: 0, height: 0 };

  // Render default logo (Primary, rightmost)
  if (defaultLogo && defaultDims.width > 0) {
    const defaultX = rect.x + rect.width - defaultDims.width;
    try {
      const resolvedUrl = getResolvedUrl(defaultLogo.url);
      const format = guessImageFormat(resolvedUrl);
      pdf.addImage(
        resolvedUrl,
        format,
        defaultX,
        rect.y,
        defaultDims.width,
        defaultDims.height,
      );
    }
    catch {
      // Logo load failed silently — skip
    }
  }

  // Render added logo (Secondary, left of default logo)
  if (addedLogo && addedDims.width > 0) {
    let addedX = rect.x + rect.width - addedDims.width;
    if (defaultLogo && defaultDims.width > 0) {
      addedX = rect.x + rect.width - defaultDims.width - gap - addedDims.width;
    }
    try {
      const resolvedUrl = getResolvedUrl(addedLogo.url);
      const format = guessImageFormat(resolvedUrl);
      pdf.addImage(
        resolvedUrl,
        format,
        addedX,
        rect.y,
        addedDims.width,
        addedDims.height,
      );
    }
    catch {
      // Logo load failed silently — skip
    }
  }

  return y + theme.spacing.sectionGap + targetHeight / 2;
}

function renderFieldGroupSection(
  pdf: PdfPx,
  section: FieldGroupSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Title
  if (section.title) {
    y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
  }

  // Rows
  const labelFont = theme.fonts.label;
  const valueFont = theme.fonts.value;
  const { fieldGap } = theme.spacing;

  for (const row of section.rows) {
    const totalWidth = row.reduce((acc, f) => acc + (f.width ?? 1), 0);
    let xOffset = rect.x;

    // First pass: wrap lines and calculate heights
    const rowData = row.map((field) => {
      const frac = (field.width ?? 1) / totalWidth;
      const cellW = rect.width * frac;
      const wrapW = cellW - 6; // Leave a 6px gap to avoid text touching the next cell

      // Wrap label
      let labelLines: string[] = [];
      if (field.label) {
        applyFont(pdf, labelFont, theme.colors.text);
        labelLines = pdf.splitTextToSize(field.label, wrapW);
      }
      const labelH = getTextBlockHeight(
        labelLines.length,
        labelFont.size,
        1.25,
      );

      // Wrap value
      let display: string;
      if (field.type === 'dropdown') {
        display = resolveDropdownLabel(values, field);
      }
      else if (field.type === 'date') {
        const raw = resolveValue(values, field);
        display = raw || '—';
      }
      else {
        display = resolveValue(values, field) || '—';
      }

      if (field.unit) {
        display = `${display} ${field.unit}`;
      }

      applyFont(pdf, valueFont, theme.colors.text);
      const valueLines = pdf.splitTextToSize(display, wrapW);
      const valueH = getTextBlockHeight(
        valueLines.length,
        valueFont.size,
        1.25,
      );

      return {
        field,
        cellW,
        labelLines,
        labelH,
        valueLines,
        valueH,
      };
    });

    const maxLabelH = Math.max(...rowData.map(d => d.labelH));
    const maxValueH = Math.max(...rowData.map(d => d.valueH));

    // Second pass: render the row elements and advance xOffset
    for (const data of rowData) {
      if (data.labelLines.length > 0) {
        applyFont(pdf, labelFont, theme.colors.text);
        pdf.text(data.labelLines, xOffset, y);
      }

      const valY = y + maxLabelH + (maxLabelH > 0 ? 3 : 0);
      applyFont(pdf, valueFont, theme.colors.text);
      pdf.setFont(valueFont.family, 'bold');
      pdf.text(data.valueLines, xOffset, valY);

      xOffset += data.cellW;
    }

    const rowH = maxLabelH + (maxLabelH > 0 ? 3 : 0) + maxValueH;
    y += rowH + fieldGap + theme.spacing.sectionGap * 0.5;
  }

  y -= theme.spacing.sectionGap * 0.5;
  return y;
}

function renderFreeTextSection(
  pdf: PdfPx,
  section: FreeTextSection,
  _values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  if (section.title) {
    y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
  }

  applyFont(pdf, theme.fonts.body, theme.colors.muted);
  pdf.setFont(theme.fonts.body.family, 'italic');
  const lines = pdf.splitTextToSize(section.text, rect.width);
  pdf.text(lines, rect.x, y);
  y += lines.length * theme.fonts.body.size * 1.25;

  return y + theme.spacing.sectionGap;
}

function renderRichTextSection(
  pdf: PdfPx,
  section: RichTextSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Title bar
  if (section.title) {
    y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
  }

  // Content (multiline text)
  const content
    = resolveValue(values, section.field) || section.field.placeholder || '';
  applyFont(pdf, theme.fonts.body, theme.colors.text);
  const lines = pdf.splitTextToSize(content, rect.width);
  pdf.text(lines, rect.x, y);
  y += lines.length * theme.fonts.body.size * 1.3;

  return y + theme.spacing.sectionGap;
}

function renderStatRowSection(
  pdf: PdfPx,
  section: StatRowSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Title + unit
  if (section.title) {
    const titleText = section.unit
      ? `${section.title} (${section.unit})`
      : section.title;
    y = renderSectionTitle(pdf, titleText, theme, rect.x, y, rect.width);
  }

  // Stat fields in a horizontal row
  const statW = rect.width / section.stats.length;
  const labelFont = theme.fonts.label;
  const valueFont = theme.fonts.value;

  for (let i = 0; i < section.stats.length; i++) {
    const stat = section.stats[i];
    const sx = rect.x + i * statW;

    // Label
    applyFont(pdf, labelFont, theme.colors.muted);
    pdf.text(stat.label || '', sx, y);

    // Value
    const val = resolveValue(values, stat) || '—';
    applyFont(pdf, valueFont, theme.colors.text);
    pdf.setFont(valueFont.family, 'bold');
    pdf.text(val, sx, y + labelFont.size + 2);
  }

  y += labelFont.size + valueFont.size + 6;
  return y + theme.spacing.sectionGap;
}

// function renderTableSection(
//   pdf: PdfPx,
//   section: TableSection,
//   values: Record<string, FieldState>,
//   theme: Theme,
//   rect: Rect
// ): number {
//   let { y } = rect;

//   // Title
//   if (section.title) {
//     y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
//     y += 2;
//   }

//   const colCount = section.columns.length;
//   const rowH = theme.fonts.value.size + 8;
//   const headerH = theme.fonts.label.size + 8;
//   const cellPadX = 6; // Slightly increased for better readability
//   const cellPadY = theme.fonts.label.size + 2;

//   // ── 1. Measure Column Content Widths ──
//   const minColWidths = new Array(colCount).fill(0);

//   // Measure Header Widths
//   applyFont(pdf, theme.fonts.label, theme.colors.text);
//   pdf.setFont(theme.fonts.label.family, 'bold');
//   for (let c = 0; c < colCount; c++) {
//     const textWidth = pdf.getTextWidth(section.columns[c]) + cellPadX * 2;
//     minColWidths[c] = Math.max(minColWidths[c], textWidth);
//   }

//   // Pre-resolve and measure cell widths
//   const resolvedRows = section.rows.map((row) => {
//     const cells = row.cells.map((cell) => resolveValue(values, cell) || '—');
//     return { label: row.label, cells };
//   });

//   resolvedRows.forEach((row) => {
//     // Measure Row Label (Column 0)
//     applyFont(pdf, theme.fonts.label, theme.colors.text);
//     const labelWidth = pdf.getTextWidth(row.label) + cellPadX * 2;
//     minColWidths[0] = Math.max(minColWidths[0], labelWidth);

//     // Measure Value Cells (Columns 1 to N)
//     applyFont(pdf, theme.fonts.value, theme.colors.text);
//     row.cells.forEach((cellVal, ci) => {
//       const colIndex = ci + 1;
//       if (colIndex < colCount) {
//         const cellWidth = pdf.getTextWidth(cellVal) + cellPadX * 2;
//         minColWidths[colIndex] = Math.max(minColWidths[colIndex], cellWidth);
//       }
//     });
//   });

//   // ── 2. Distribute Remaining Width Proportionally ──
//   const totalMinWidth = minColWidths.reduce((sum, w) => sum + w, 0);
//   const actualColWidths = [...minColWidths];

//   if (totalMinWidth < rect.width) {
//     const extraSpace = rect.width - totalMinWidth;
//     // Distribute remaining space based on how much space the column already requested
//     for (let c = 0; c < colCount; c++) {
//       const proportion = minColWidths[c] / totalMinWidth;
//       actualColWidths[c] += extraSpace * proportion;
//     }
//   } else {
//     // Fallback: If content overflows total width, scale down proportionally to fit container
//     for (let c = 0; c < colCount; c++) {
//       actualColWidths[c] = (minColWidths[c] / totalMinWidth) * rect.width;
//     }
//   }

//   // Precompute column X coordinates for quick rendering lookups
//   const colPositions = [rect.x];
//   for (let c = 0; c < colCount; c++) {
//     colPositions.push(colPositions[c] + actualColWidths[c]);
//   }

//   // ── 3. Render Header Row ──
//   pdf.setFillColor(theme.colors.tableHeaderBg);
//   pdf.rect(rect.x, y, rect.width, headerH, 'F');

//   pdf.setDrawColor(theme.colors.border);
//   pdf.setLineWidth(0.3);
//   pdf.rect(rect.x, y, rect.width, headerH, 'S');

//   applyFont(pdf, theme.fonts.label, theme.colors.text);
//   pdf.setFont(theme.fonts.label.family, 'bold');
//   for (let c = 0; c < colCount; c++) {
//     pdf.text(section.columns[c], colPositions[c] + cellPadX, y + cellPadY);
//     if (c > 0) {
//       pdf.line(colPositions[c], y, colPositions[c], y + headerH);
//     }
//   }
//   y += headerH;

//   // ── 4. Render Data Rows ──
//   resolvedRows.forEach((row) => {
//     pdf.setDrawColor(theme.colors.border);
//     pdf.setLineWidth(0.3);
//     pdf.rect(rect.x, y, rect.width, rowH, 'S');

//     // Vertical dividers
//     for (let c = 1; c < colCount; c++) {
//       pdf.line(colPositions[c], y, colPositions[c], y + rowH);
//     }

//     // Row label (Column 0)
//     applyFont(pdf, theme.fonts.label, theme.colors.text);
//     pdf.text(
//       row.label,
//       colPositions[0] + cellPadX,
//       y + theme.fonts.label.size + 2
//     );

//     // Cell values (Columns 1 to N)
//     applyFont(pdf, theme.fonts.value, theme.colors.text);
//     row.cells.forEach((val, ci) => {
//       const colIndex = ci + 1;
//       if (colIndex < colCount) {
//         pdf.text(
//           val,
//           colPositions[colIndex] + cellPadX,
//           y + theme.fonts.value.size + 2
//         );
//       }
//     });

//     y += rowH;
//   });

//   // Footnote
//   if (section.footnote) {
//     y += 12;
//     applyFont(
//       pdf,
//       { ...theme.fonts.body, size: theme.fonts.body.size - 1 },
//       theme.colors.muted
//     );
//     pdf.setFont(theme.fonts.body.family, 'italic');
//     const fLines = pdf.splitTextToSize(section.footnote, rect.width);

//     pdf.text(fLines, rect.x, y);
//     y += fLines.length * (theme.fonts.body.size - 1) * 1.3;
//   }

//   return y + theme.spacing.sectionGap;
// }

function renderTableSection(
  pdf: PdfPx,
  section: TableSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Title
  if (section.title) {
    y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
    y += 2;
  }

  const colCount = section.columns.length;
  const rowH = theme.fonts.value.size + 8;
  const headerH = theme.fonts.label.size + 8;
  const cellPadX = 6; // Slightly increased for better readability
  const cellPadY = theme.fonts.label.size + 2;

  // ── 1. Measure Column Content Widths ──
  const minColWidths: number[] = Array.from({ length: colCount }).fill(0) as number[];

  // Measure Header Widths
  applyFont(pdf, theme.fonts.label, theme.colors.text);
  pdf.setFont(theme.fonts.label.family, 'bold');
  for (let c = 0; c < colCount; c++) {
    const textWidth = pdf.getTextWidth(section.columns[c]) + cellPadX * 2;
    minColWidths[c] = Math.max(minColWidths[c], textWidth);
  }

  // Pre-resolve, filter out completely empty rows, and measure cell widths
  const resolvedRows = section.rows
    .map((row) => {
      // Keep track of raw resolved values to easily check if they are all empty
      const rawCells = row.cells.map(cell => resolveValue(values, cell));

      // Determine if every data cell in this row is empty/falsy
      const isEmptyRow = rawCells.every(val => !val || val.trim() === '');

      return {
        label: row.label,
        cells: rawCells.map(val => val || '—'),
        isEmptyRow,
      };
    })
    // Skip rendering if the row has no data
    .filter(row => !row.isEmptyRow);

  resolvedRows.forEach((row) => {
    // Measure Row Label (Column 0)
    applyFont(pdf, theme.fonts.label, theme.colors.text);
    const labelWidth = pdf.getTextWidth(row.label) + cellPadX * 2;
    minColWidths[0] = Math.max(minColWidths[0], labelWidth);

    // Measure Value Cells (Columns 1 to N)
    applyFont(pdf, theme.fonts.value, theme.colors.text);
    row.cells.forEach((cellVal, ci) => {
      const colIndex = ci + 1;
      if (colIndex < colCount) {
        const cellWidth = pdf.getTextWidth(cellVal) + cellPadX * 2;
        minColWidths[colIndex] = Math.max(minColWidths[colIndex], cellWidth);
      }
    });
  });

  // ── 2. Distribute Remaining Width Proportionally ──
  const totalMinWidth = minColWidths.reduce((sum, w) => sum + w, 0);
  const actualColWidths = [...minColWidths];

  if (totalMinWidth < rect.width) {
    const extraSpace = rect.width - totalMinWidth;
    for (let c = 0; c < colCount; c++) {
      const proportion = minColWidths[c] / totalMinWidth;
      actualColWidths[c] += extraSpace * proportion;
    }
  }
  else {
    for (let c = 0; c < colCount; c++) {
      actualColWidths[c] = (minColWidths[c] / totalMinWidth) * rect.width;
    }
  }

  // Precompute column X coordinates for quick rendering lookups
  const colPositions = [rect.x];
  for (let c = 0; c < colCount; c++) {
    colPositions.push(colPositions[c] + actualColWidths[c]);
  }

  // ── 3. Render Header Row ──
  pdf.setFillColor(theme.colors.tableHeaderBg);
  pdf.rect(rect.x, y, rect.width, headerH, 'F');

  pdf.setDrawColor(theme.colors.border);
  pdf.setLineWidth(0.3);
  pdf.rect(rect.x, y, rect.width, headerH, 'S');

  applyFont(pdf, theme.fonts.label, theme.colors.text);
  pdf.setFont(theme.fonts.label.family, 'bold');
  for (let c = 0; c < colCount; c++) {
    pdf.text(section.columns[c], colPositions[c] + cellPadX, y + cellPadY);
    if (c > 0) {
      pdf.line(colPositions[c], y, colPositions[c], y + headerH);
    }
  }
  y += headerH;

  // ── 4. Render Data Rows ──
  resolvedRows.forEach((row) => {
    pdf.setDrawColor(theme.colors.border);
    pdf.setLineWidth(0.3);
    pdf.rect(rect.x, y, rect.width, rowH, 'S');

    // Vertical dividers
    for (let c = 1; c < colCount; c++) {
      pdf.line(colPositions[c], y, colPositions[c], y + rowH);
    }

    // Row label (Column 0)
    applyFont(pdf, theme.fonts.label, theme.colors.text);
    pdf.text(
      row.label,
      colPositions[0] + cellPadX,
      y + theme.fonts.label.size + 2,
    );

    // Cell values (Columns 1 to N)
    applyFont(pdf, theme.fonts.value, theme.colors.text);
    row.cells.forEach((val, ci) => {
      const colIndex = ci + 1;
      if (colIndex < colCount) {
        pdf.text(
          val,
          colPositions[colIndex] + cellPadX,
          y + theme.fonts.value.size + 2,
        );
      }
    });

    y += rowH;
  });

  // Footnote
  if (section.footnote) {
    y += 12;
    applyFont(
      pdf,
      { ...theme.fonts.body, size: theme.fonts.body.size - 1 },
      theme.colors.muted,
    );
    pdf.setFont(theme.fonts.body.family, 'italic');
    const fLines = pdf.splitTextToSize(section.footnote, rect.width);

    pdf.text(fLines, rect.x, y);
    y += fLines.length * (theme.fonts.body.size - 1) * 1.3;
  }

  return y + theme.spacing.sectionGap;
}
/** Draw a dashed border placeholder where an image would go */
function drawImagePlaceholder(
  pdf: PdfPx,
  theme: Theme,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  // Fill background
  pdf.setFillColor(theme.imagePlaceholder.background);
  pdf.rect(x, y, w, h, 'F');

  // Dashed or solid border
  pdf.setDrawColor(theme.imagePlaceholder.borderColor);
  pdf.setLineWidth(0.5);
  if (theme.imagePlaceholder.borderStyle === 'dashed') {
    pdf.setLineDashPattern([3, 3], 0);
  }
  pdf.rect(x, y, w, h, 'S');
  pdf.setLineDashPattern([], 0);

  // Center text
  applyFont(pdf, theme.fonts.label, theme.colors.muted);
  const label = 'No Image';
  const tw = pdf.getTextWidth(label);
  pdf.text(label, x + (w - tw) / 2, y + h / 2 + theme.fonts.label.size / 3);
}

function renderImageGridSection(
  pdf: PdfPx,
  section: ImageGridSection,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  let { y } = rect;

  // Title
  if (section.title) {
    y = renderSectionTitle(pdf, section.title, theme, rect.x, y, rect.width);
    y += 2;
  }

  const gap = theme.spacing.fieldGap;
  const cols = section.columns;
  const cellW = (rect.width - gap * (cols - 1)) / cols;
  const imgH = section.imageHeight;

  // Group cells into rows
  const gridRows: (typeof section.cells)[] = [];
  for (let i = 0; i < section.cells.length; i += cols) {
    gridRows.push(section.cells.slice(i, i + cols));
  }

  for (let r = 0; r < gridRows.length; r++) {
    const rowCells = gridRows[r];

    // First pass: wrap titles and compute heights for this row
    const cellData = rowCells.map((cell) => {
      const title
        = resolveValue(values, cell.titleField)
          || cell.titleField.placeholder
          || '';
      applyFont(pdf, theme.fonts.label, theme.colors.text);
      const titleLines = pdf.splitTextToSize(title, cellW - 6);
      const titleH = getTextBlockHeight(
        titleLines.length,
        theme.fonts.label.size,
        1.25,
      );
      return {
        cell,
        titleLines,
        titleH,
      };
    });

    const maxTitleH = Math.max(...cellData.map(d => d.titleH));
    const titlePadding = maxTitleH > 0 ? 4 : 0;
    const currentTitleBlockH = maxTitleH + titlePadding;

    // Second pass: render titles and images aligned

    cellData.forEach((data, colIdx) => {
      const cx = rect.x + colIdx * (cellW + gap);

      // Draw title
      if (data.titleLines.length > 0) {
        applyFont(pdf, theme.fonts.label, theme.colors.text);
        pdf.text(data.titleLines, cx, y);
      }

      // Draw image
      const imgY = y + currentTitleBlockH;
      const imageData = resolveValue(values, data.cell.imageField);

      if (imageData && imageData.length > 10) {
        try {
          const format = guessImageFormat(imageData);
          pdf.addImage(imageData, format, cx, imgY, cellW, imgH);
        }
        catch {
          drawImagePlaceholder(pdf, theme, cx, imgY, cellW, imgH);
        }
      }
      else {
        drawImagePlaceholder(pdf, theme, cx, imgY, cellW, imgH);
      }
    });

    y += currentTitleBlockH + imgH + gap;
  }

  return y - gap + theme.spacing.sectionGap;
}

// ─── Section Dispatch ────────────────────────────────────────────────────────

/**
 * Renders a single section and returns the new Y after it.
 */
function renderSection(
  pdf: PdfPx,
  section: Section,
  values: Record<string, FieldState>,
  theme: Theme,
  rect: Rect,
): number {
  switch (section.kind) {
    case 'header':
      return renderHeaderSection(pdf, section, values, theme, rect);
    case 'fieldGroup':
      return renderFieldGroupSection(pdf, section, values, theme, rect);
    case 'freeText':
      return renderFreeTextSection(pdf, section, values, theme, rect);
    case 'richText':
      return renderRichTextSection(pdf, section, values, theme, rect);
    case 'statRow':
      return renderStatRowSection(pdf, section, values, theme, rect);
    case 'table':
      return renderTableSection(pdf, section, values, theme, rect);
    case 'imageGrid':
      return renderImageGridSection(pdf, section, values, theme, rect);
    default:
      return rect.y;
  }
}

// ─── Panel Rendering ─────────────────────────────────────────────────────────

/**
 * Renders a single panel (column) — draws background, then iterates sections.
 */
function renderPanel(
  pdf: PdfPx,
  panel: Panel,
  values: Record<string, FieldState>,
  theme: Theme,
  panelRect: Rect,
) {
  // Panel background
  if (panel.background) {
    pdf.setFillColor(panel.background);
    pdf.rect(panelRect.x, panelRect.y, panelRect.width, panelRect.height, 'F');
  }

  const { padding } = panel;
  const { top, bottom, left, right } = padding;
  const { gap } = panel;

  // Content rect inside padding
  const contentRect: Rect = {
    x: panelRect.x + left,
    y: panelRect.y + top,
    width: panelRect.width - left - right,
    height: panelRect.height - top - bottom,
  };

  let { y } = contentRect;

  for (const section of panel.sections) {
    const sectionRect: Rect = {
      x: contentRect.x,
      y,
      width: contentRect.width,
      height: contentRect.height - (y - contentRect.y),
    };

    y = renderSection(pdf, section, values, theme, sectionRect);
    y += gap;
  }
}

// ─── Main Entry Point ────────────────────────────────────────────────────────

/**
 * Build a text-selectable PDF from a ReportTemplate and current values.
 *
 * @param template  The report template (layout + theme + sections)
 * @param values    The current field state map from the store
 * @returns         A jsPDF instance ready for `.save()` or `.output()`
 */
export function renderTemplateToPDF(
  template: ReportTemplate,
  values: Record<string, FieldState>,
): jsPDF {
  const { layout, theme } = template;

  // Create doc in pt, A4 portrait — but note we never touch pt again below.
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });
  const pdf = new PdfPx(doc);

  // Everything from here down is template px, matching the HTML preview.
  // PdfPx converts to pt only at the moment a value reaches jsPDF.
  const { margin } = layout;
  const colGap = layout.columnGap;

  const contentW = A4_PX.width - margin * 2;
  const contentH = A4_PX.height - margin * 2;

  // Compute panel rects
  const panelRects: Rect[] = [];
  let xCursor = margin;

  for (let i = 0; i < layout.panels.length; i++) {
    const panel = layout.panels[i];
    const panelW
      = contentW * panel.width - (i < layout.panels.length - 1 ? colGap / 2 : 0);

    panelRects.push({
      x: xCursor,
      y: margin,
      width: panelW,
      height: contentH,
    });

    xCursor += panelW + colGap;
  }

  // Render each panel
  for (let i = 0; i < layout.panels.length; i++) {
    renderPanel(pdf, layout.panels[i], values, theme, panelRects[i]);
  }

  return doc;
}

/**
 * Convenience: render and immediately trigger a download.
 */
export function downloadReportAsPDF(
  template: ReportTemplate,
  values: Record<string, FieldState>,
  filename = 'report.pdf',
) {
  const doc = renderTemplateToPDF(template, values);
  doc.save(filename);
}

/**
 * Render multiple report pages into a single, continuous multi-page PDF.
 *
 * Each entry in the `pages` array gets its own PDF page. The first entry
 * uses the initial page created by `new jsPDF()`; subsequent entries call
 * `doc.addPage()`.
 *
 * @param pages     Array of { template, values } objects — one per report page
 * @param filename  Output filename (default: 'report.pdf')
 */
export function downloadMultiReportAsPDF(
  pages: Array<{
    template: ReportTemplate;
    values: Record<string, FieldState>;
  }>,
  filename = 'report.pdf',
) {
  if (pages.length === 0) return;

  // Use the first page to bootstrap the jsPDF instance
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });
  const pdf = new PdfPx(doc);

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const { template, values } = pages[pageIndex];
    const { layout, theme } = template;

    // Add a new PDF page for every page after the first
    if (pageIndex > 0) {
      doc.addPage('a4', 'portrait');
    }

    const { margin } = layout;
    const colGap = layout.columnGap;

    const contentW = A4_PX.width - margin * 2;
    const contentH = A4_PX.height - margin * 2;

    // Compute panel rects
    const panelRects: Rect[] = [];
    let xCursor = margin;

    for (let i = 0; i < layout.panels.length; i++) {
      const panel = layout.panels[i];
      const panelW
        = contentW * panel.width
          - (i < layout.panels.length - 1 ? colGap / 2 : 0);

      panelRects.push({
        x: xCursor,
        y: margin,
        width: panelW,
        height: contentH,
      });

      xCursor += panelW + colGap;
    }

    // Render each panel
    for (let i = 0; i < layout.panels.length; i++) {
      renderPanel(pdf, layout.panels[i], values, theme, panelRects[i]);
    }
  }

  doc.save(filename);
}
